import React, { useState, useEffect } from "react";
import {
  PublicClientApplication,
  AccountInfo,
  Configuration,
  AuthenticationResult,
  EventMessage,
  EventType,
  LogLevel
} from "@azure/msal-browser";
import { Application } from "@microsoft/microsoft-graph-types";
import {
  Container,
  Button,
  Typography,
  Box,
  TextField,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Divider,
  Chip,
  Grid,
  Alert,
  IconButton,
  Stack
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CodeIcon from "@mui/icons-material/Code";
import TokenIcon from "@mui/icons-material/Token";
import SecurityIcon from "@mui/icons-material/Security";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import PersonIcon from "@mui/icons-material/Person";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AssessmentIcon from "@mui/icons-material/Assessment";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import DoubleArrowIcon from "@mui/icons-material/DoubleArrow";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { AlertTitle } from "@mui/material";
import DevicesIcon from '@mui/icons-material/Devices';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { Link, CircularProgress } from '@mui/material';
import { Application as MsGraphApplication } from "@microsoft/microsoft-graph-types";
import { API_CONFIG, isDevelopment } from '../config/environment';

/** Manual decoder (no external library) */
function decodeJwtManually(token: string) {
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length !== 3) {
    console.warn("Token doesn't look like a standard JWT:", token);
    return null;
  }
  try {
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const base64 = atob(payload);
    const jsonPayload = decodeURIComponent(
      Array.from(base64)
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (err) {
    console.error("Error decoding token:", err);
    return null;
  }
}

// First, define a helper type to represent nullable properties
type NullableOption<T> = T | null | undefined;

// Extended Application interface with additional properties
interface ExtendedApplication extends Omit<Application, 'signInAudience'> {
  appOwnerTenantId?: string;
  signInAudience?: string | null | undefined;
  publisherDomain?: string;
  identifierUris?: string[];
}

// Update props interface
interface AuthFlowTesterProps {
  app: ExtendedApplication;
  onBack: () => void;
  onDecodedTokenTested?: (decodedToken: any) => void;
}

const discoverTenantId = async (
  app: ExtendedApplication, 
  testerAccount: AccountInfo | null,
  graphAccessToken?: string
): Promise<string> => {
  // For multi-tenant apps, use organizations endpoint
  if (app.signInAudience !== "AzureADMyOrg") {
    return "organizations";
  }

  // For single-tenant apps, try multiple sources in order of preference
  
  // 1. Current user's tenant (most reliable if user is signed in)
  if (testerAccount?.tenantId) {
    console.log(`Using current user's tenant: ${testerAccount.tenantId}`);
    return testerAccount.tenantId;
  }

  // 2. App owner tenant ID from app registration
  if ((app as any).appOwnerTenantId) {
    console.log(`Using app owner tenant: ${(app as any).appOwnerTenantId}`);
    return (app as any).appOwnerTenantId;
  }

  // 3. Try to get tenant from Microsoft Graph if we have an access token
  if (graphAccessToken) {
    try {
      const response = await fetch('https://graph.microsoft.com/v1.0/organization', {
        headers: {
          'Authorization': `Bearer ${graphAccessToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const orgData = await response.json();
        if (orgData.value && orgData.value.length > 0) {
          const tenantId = orgData.value[0].id;
          console.log(`Discovered tenant from Graph API: ${tenantId}`);
          return tenantId;
        }
      }
    } catch (error) {
      console.warn('Failed to discover tenant from Graph API:', error);
    }
  }

  // 4. Try to extract from publisher domain
  if (app.publisherDomain && app.publisherDomain !== 'microsoftonline.com') {
    console.log(`Using publisher domain as tenant: ${app.publisherDomain}`);
    return app.publisherDomain;
  }

  // 5. Try to extract from identifier URIs
  if (app.identifierUris && app.identifierUris.length > 0) {
    for (const uri of app.identifierUris) {
      // Extract tenant from URIs like "api://tenant-id/app-id" or "https://tenant.onmicrosoft.com/app-id"
      const tenantMatch = uri.match(/^(?:api:\/\/|https:\/\/)([a-f0-9-]{36}|[^\/]+\.onmicrosoft\.com)/i);
      if (tenantMatch && tenantMatch[1]) {
        console.log(`Extracted tenant from identifier URI: ${tenantMatch[1]}`);
        return tenantMatch[1];
      }
    }
  }

  // 6. Last resort: prompt user or throw error
  throw new Error(
    'Unable to determine tenant ID for single-tenant application. ' +
    'Please ensure you are signed in or the application has proper tenant information configured.'
  );
};

/** Track which flow is active, to disable others. */
type ActiveFlow = "none" | "authCode" | "hybrid" | "implicit" | "clientCredentials";

const AuthFlowTester: React.FC<AuthFlowTesterProps> = ({
  app,
  onBack,
  onDecodedTokenTested
}) => {
  const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null);
  const [testerAccount, setTesterAccount] = useState<AccountInfo | null>(null);

  const [flowResult, setFlowResult] = useState<string>("MSAL not initialized yet");
  const [flowStepsLog, setFlowStepsLog] = useState<string>("");
  const [clientSecret, setClientSecret] = useState("");
  const [decodedToken, setDecodedToken] = useState<any>(null);
  const [isTokenVisible, setIsTokenVisible] = useState(false);

  /** Which flow is currently “locked in” and should disable others */
  const [activeFlow, setActiveFlow] = useState<ActiveFlow>("none");
  const [showFlowInfo, setShowFlowInfo] = useState(false);

  // Track real MSAL events for Flow Status display
  const [flowEvents, setFlowEvents] = useState<Array<{
    eventType: EventType;
    timestamp: Date;
    message: string;
    correlationId?: string;
  }>>([]);

  // State for hybrid flow details
  const [hybridDetailsExpanded, setHybridDetailsExpanded] = useState(false);
  const [implicitDetailsExpanded, setImplicitDetailsExpanded] = useState(false);
  const [aboutExpanded, setAboutExpanded] = useState(false);

  // Enhanced appendFlowLog function with optional type parameter for styling
  function appendFlowLog(line: string, type: "normal" | "system" | "warning" | "success" = "normal") {
    // Create timestamp and format message
    const now = new Date();
    const timestamp = now.toLocaleTimeString([], { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const formattedLine = `[${timestamp}] ${line}`;
    
    // Add markup for later rendering with different styles
    const taggedLine = type === "system" 
      ? `<system>${formattedLine}</system>` 
      : type === "warning"
        ? `<warning>${formattedLine}</warning>`
        : type === "success" 
          ? `<success>${formattedLine}</success>`
          : formattedLine;

    // Add sequential ordering using closure to ensure proper sequence
    const orderedLine = `${taggedLine}`;
          
    setFlowStepsLog((prev) => prev + "\n" + taggedLine);
  }

  /** 
   * Determines the correct authority URL based on app configuration.
   * Enhanced to better support both single and multi-tenant apps.
   */
  function getAuthorityForApp(): string {
    // For single-tenant apps, use tenant-specific endpoint
    if (app.signInAudience === "AzureADMyOrg") {
      // Try to get tenant ID from app properties
      if (app.publisherDomain) {
        console.log("Using publisherDomain for authority:", app.publisherDomain);
        return `https://login.microsoftonline.com/${app.publisherDomain}`;
      } else if (testerAccount?.tenantId) {
        // Try logged-in user's tenant - can be important for consistency
        console.log("Using current account tenant for authority:", testerAccount.tenantId);
        return `https://login.microsoftonline.com/${testerAccount.tenantId}`;
      } else if ((app as any).appOwnerTenantId) {
        console.log("Using appOwnerTenantId for authority:", (app as any).appOwnerTenantId);
        return `https://login.microsoftonline.com/${(app as any).appOwnerTenantId}`;
      } else if (app.identifierUris && app.identifierUris.length > 0) {
        // Try to extract tenant from identifier URI
        const uri = app.identifierUris[0];
        const match = uri.match(/https:\/\/([^\/]+)/);
        if (match && match[1]) {
          console.log("Using extracted domain from identifierUri for authority:", match[1]);
          return `https://login.microsoftonline.com/${match[1]}`;
        }
      }
      
      // If we can't determine the tenant for a single-tenant app, throw an error
      console.error("Unable to determine tenant for single-tenant application");
      throw new Error(
        "Unable to determine tenant ID for single-tenant application. " +
        "Please ensure the application has proper tenant information configured or sign in first."
      );
    } else {
      // Multi-tenant => use common endpoint
      console.log("Using multi-tenant endpoint");
      return "https://login.microsoftonline.com/common";
    }
  }

  /** Initialize MSAL once. */
  useEffect(() => {
    if (!app.appId) {
      setFlowResult("No appId found on this Application object.");
      return;
    }

    const authority = getAuthorityForApp();
    const msalConfig: Configuration = {
      auth: {
        clientId: app.appId,
        authority,
        redirectUri: window.location.origin
      },
      cache: {
        // CRITICAL: Use localStorage to isolate test app from main app's sessionStorage
        // This prevents test app logout from clearing main app tokens
        cacheLocation: "localStorage",
        storeAuthStateInCookie: false
      },
      system: {
        loggerOptions: {
          loggerCallback: (level, message) => {
            // Keep or remove logs as you like
            if (message.includes("block_nested_popups")) return;
            console.log(`[TestedAppMSAL][${LogLevel[level]}]`, message);
          }
        }
      }
    };

    const instance = new PublicClientApplication(msalConfig);
    instance
      .initialize()
      .then(() => {
        setMsalInstance(instance);
        setFlowResult("MSAL initialized!");

        // Enhanced MSAL event callback to capture all events for Flow Status
        const callbackId = instance.addEventCallback((event: EventMessage) => {
          // Capture all MSAL events for the Flow Status display
          const eventMessage = event.error 
            ? ('errorMessage' in event.error 
                ? event.error.errorMessage 
                : event.error.message || "Event occurred")
            : "Event occurred";

          setFlowEvents(prev => [...prev, {
            eventType: event.eventType,
            timestamp: new Date(),
            message: eventMessage,
            correlationId: (event.payload as any)?.correlationId
          }]);

          // Also keep selective logging to flow steps for backward compatibility
          switch (event.eventType) {
            case EventType.LOGIN_SUCCESS:
              appendFlowLog("MSAL Event: Authentication successful", "system");
              break;
            case EventType.LOGIN_FAILURE:
              appendFlowLog(`MSAL Event: Authentication failed - ${eventMessage}`, "system");
              break;
            case EventType.ACQUIRE_TOKEN_FAILURE:
              appendFlowLog(`MSAL Event: Token acquisition failed - ${eventMessage}`, "system");
              break;
            default:
              break;
          }
        });

        return () => {
          if (callbackId) {
            instance.removeEventCallback(callbackId);
          }
        };
      })
      .catch((err) => {
        setFlowResult("MSAL initialization failed. See console.");
        console.error("MSAL init error:", err);
      });
  }, [app.appId]);

  /** Common sign-out from tested app. */
  const handleSignOut = async () => {
    if (!msalInstance || !testerAccount) {
      alert("No tested app session or no user account to sign out.");
      return;
    }
    try {
      await msalInstance.logoutPopup({
        account: testerAccount,
        postLogoutRedirectUri: `${window.location.origin}/silent-end.html`
      });
    } catch (error) {
      console.error("Sign out error:", error);
    } finally {
      setTesterAccount(null);
      setDecodedToken(null);
      setIsTokenVisible(false);
      setFlowStepsLog("");
      setFlowResult("");
      setFlowEvents([]);
      setActiveFlow("none");
    }
  };

  /** Auth Code Flow (PKCE) */
  const testAuthCodeFlow = async () => {
    if (!msalInstance) {
      setFlowResult("MSAL instance not ready!");
      return;
    }
    setFlowStepsLog("");
    setFlowResult("Starting PKCE Auth Code Flow...");
    setActiveFlow("authCode");
    setFlowEvents([]); // Clear previous events

    const authority = getAuthorityForApp();
    appendFlowLog(`Using authority: ${authority}`, "system");

    try {
      // Step 1) popup sign-in
      appendFlowLog("Step 1: Starting user authentication with popup", "normal");

      // This will trigger a popup and wait for login
      appendFlowLog("Waiting for user to authenticate in popup...", "normal");
      const loginResponse: AuthenticationResult = await msalInstance.loginPopup({
        scopes: ["openid", "profile", "User.Read"],
        prompt: "select_account"
      });
  
      appendFlowLog("✓ Authorization response received with code", "success");
      appendFlowLog("✓ User authenticated successfully", "success");
      setTesterAccount(loginResponse.account || null);

      // Step 2) Acquire token silently
      appendFlowLog("Step 2: Starting token acquisition using the auth code", "normal");
      appendFlowLog("Redeeming authorization code for tokens...", "normal");
      const tokenResponse = await msalInstance.acquireTokenSilent({
        scopes: ["User.Read"],
        account: loginResponse.account
      });
  
      appendFlowLog("✓ Code exchanged successfully", "success");
      appendFlowLog("✓ Access token and ID token received", "success");

      const localDecoded = decodeJwtManually(tokenResponse.accessToken);
      setDecodedToken(localDecoded);
      if (onDecodedTokenTested) {
        onDecodedTokenTested(localDecoded);
      }

      setFlowResult("Auth Code Flow success! Check the flow log below.");
    } catch (error: any) {
      setFlowResult(`Auth Code Flow FAILED\n${error.message}`);
      console.error("Auth Code Flow error:", error);
      setActiveFlow("none");
    }
  };

  /** Hybrid Flow => request response_type=code id_token (plus PKCE) */
  const testHybridFlow = async () => {
    if (!msalInstance) {
      setFlowResult("MSAL instance not ready!");
      return;
    }
    setFlowStepsLog("");
    setFlowResult("Starting Hybrid Flow (code + id_token)...");
    setActiveFlow("hybrid");
    setFlowEvents([]); // Clear previous events

    const authority = getAuthorityForApp();
    appendFlowLog(`Using authority: ${authority}`, "system");

    try {
      appendFlowLog("Step 1: Starting Hybrid Flow (response_type=code id_token)");
    
      // In reality, MSAL typically does the code exchange anyway,
      // but here we demonstrate how you'd request a 'code id_token'.
      appendFlowLog("Initiating authorization request with response_type=code id_token...");
      const loginResponse: AuthenticationResult = await msalInstance.loginPopup({
        scopes: ["openid", "profile", "User.Read"],
        prompt: "select_account",
        extraQueryParameters: {
          response_type: "code id_token"
        }
      });

      appendFlowLog("✓ Authorization response received with code AND ID token", "success");
      appendFlowLog("✓ ID token received immediately in authorization response", "success");
      appendFlowLog("✓ User identity established without additional calls", "success");
      setTesterAccount(loginResponse.account || null);

      // Acquire Access Token
      appendFlowLog("Step 2: Exchanging authorization code for access token...");
      const tokenResponse = await msalInstance.acquireTokenSilent({
        scopes: ["User.Read"],
        account: loginResponse.account
      });
  
      appendFlowLog("✓ Code exchanged successfully", "success");
      appendFlowLog("✓ Access token received", "success");
      appendFlowLog("✓ New ID token also received (may contain additional/updated claims)", "success");

      const localDecoded = decodeJwtManually(tokenResponse.accessToken);
      setDecodedToken(localDecoded);
      if (onDecodedTokenTested) {
        onDecodedTokenTested(localDecoded);
      }

      setFlowResult("Hybrid Flow success! You got an ID token & code (then an Access Token).");
    } catch (error: any) {
      setFlowResult(`Hybrid Flow FAILED\n${error.message}`);
      console.error("Hybrid Flow error:", error);
      setActiveFlow("none");
    }
  };

  /** Client Credentials Flow (server-to-server). You typically wouldn’t do it in the browser, but for dev/demo you might. */
  const testClientCredentialsFlow = async () => {
    if (!app.appId) {
      setFlowResult("No appId found on this Application object for client credentials flow.");
      return;
    }
    if (!clientSecret) {
      alert("Enter a client secret first.");
      return;
    }
    setFlowStepsLog("");
    setFlowResult("Starting client credentials flow...");
    setFlowEvents([]); // Clear previous events

    try {
      appendFlowLog("Step 1: Discovering target tenant", "normal");
      
      const graphAccessToken = undefined; // TODO: Get from your app's authentication context
      
      let tenantId: string;
      try {
        tenantId = await discoverTenantId(app, testerAccount, graphAccessToken);
        appendFlowLog(`✓ Tenant discovered: ${tenantId}`, "success");
        
        // Add validation logging
        appendFlowLog(`Validating inputs:`, "system");
        appendFlowLog(`- ClientId: ${app.appId} (length: ${app.appId?.length})`, "system");
        appendFlowLog(`- TenantId: ${tenantId} (length: ${tenantId.length})`, "system");
        
        // Add client-side validation to catch issues early
        const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        
        if (!guidRegex.test(app.appId!)) {
          appendFlowLog(`⚠️ ClientId format validation failed`, "warning");
        } else {
          appendFlowLog(`✓ ClientId format validated`, "success");
        }
        
        // Check tenant ID format
        const isValidTenant = guidRegex.test(tenantId) || 
                            ['common', 'organizations', 'consumers'].includes(tenantId.toLowerCase()) ||
                            /^[a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?)*$/.test(tenantId);
        
        if (!isValidTenant) {
          appendFlowLog(`⚠️ TenantId format validation failed`, "warning");
        } else {
          appendFlowLog(`✓ TenantId format validated`, "success");
        }
        
      } catch (error: any) {
        appendFlowLog(`✗ Tenant discovery failed: ${error.message}`, "warning");
        setFlowResult(`Client Credentials Flow FAILED\nTenant Discovery Error: ${error.message}`);
        setActiveFlow("none");
        return;
      }

      appendFlowLog("Step 2: Preparing client credentials request", "normal");
      appendFlowLog(`Using tenant ID: ${tenantId}`, "system");
      appendFlowLog("Step 3: Requesting access token using client credentials", "normal");

      // Updated API call to use the new Azure Function endpoint
      const apiUrl = isDevelopment 
        ? `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.GET_TOKEN}`
        : API_CONFIG.ENDPOINTS.GET_TOKEN;

      appendFlowLog(`Making request to: ${apiUrl}`, "system");

      const requestPayload = {
        clientId: app.appId,
        clientSecret: clientSecret,
        tenantId: tenantId
      };
      
      appendFlowLog(`Request payload: ${JSON.stringify(requestPayload, null, 2)}`, "system");

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(requestPayload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        let errorMessage = errorData?.error || `HTTP ${response.status}`;
        
        // Add more detailed error information
        if (errorData?.details) {
          errorMessage += `\nDetails: ${errorData.details}`;
        }
        
        // Provide helpful error message for tenant mismatch
        if (errorData?.error === "unauthorized_client") {
          errorMessage += "\n\nTenant Mismatch Detected:";
          errorMessage += `\n- App ID: ${app.appId}`;
          errorMessage += `\n- Attempted Tenant: ${tenantId}`;
          errorMessage += `\n- Discovery Method: ${getDiscoveryMethod(app, testerAccount)}`;
          errorMessage += "\n\nSuggestions:";
          errorMessage += "\n1. Sign in with an account from the correct tenant";
          errorMessage += "\n2. Verify the app registration exists in the target tenant";
          errorMessage += "\n3. Check if this is a multi-tenant application";
        }
        
        throw new Error(errorMessage);
      }

      const result = await response.json();
      if (result.error) {
        throw new Error(`${result.error}: ${result.error_description || 'Unknown error'}`);
      }

      appendFlowLog("✓ Client credentials validated", "success");
      appendFlowLog("✓ Access token acquired for application", "success");

      // Success => decode
      const localDecodedToken = decodeJwtManually(result.access_token);
      setDecodedToken(localDecodedToken);
      if (onDecodedTokenTested) {
        onDecodedTokenTested(localDecodedToken);
      }

      setFlowResult(
        `Client Credentials Flow Success!\nAccess Token: ${result.access_token.substring(0, 20)}...`
      );
      setActiveFlow("clientCredentials");
    } catch (error: any) {
      let errorMessage = `Client Credentials Flow FAILED\n${error.message}`;
      appendFlowLog(`✗ Error: ${error.message}`, "warning");
      setFlowResult(errorMessage);
      console.error("Client Credentials error:", error);
      setActiveFlow("none");
    }
  };

  // Helper function to describe how tenant was discovered (for debugging)
  const getDiscoveryMethod = (app: ExtendedApplication, testerAccount: AccountInfo | null): string => {
    if (app.signInAudience !== "AzureADMyOrg") {
      return "Multi-tenant app (using 'organizations')";
    }
    if (testerAccount?.tenantId) {
      return "Current user's tenant";
    }
    if ((app as any).appOwnerTenantId) {
      return "App owner tenant ID";
    }
    if (app.publisherDomain) {
      return "Publisher domain";
    }
    if (app.identifierUris && app.identifierUris.length > 0) {
      return "Identifier URI extraction";
    }
    return "Unknown/Fallback";
  };

  /** Exits client credentials mode. */
  function handleExitClientCredsFlow() {
    setFlowResult("Exited client credentials flow. You can run another flow now.");
    setDecodedToken(null);
    setIsTokenVisible(false);
    setActiveFlow("none");
  }

  /** Implicit Flow - Get token directly in URL without code */
  const testImplicitFlow = async () => {
    if (!msalInstance) {
      setFlowResult("MSAL instance not ready!");
      return;
    }
    setFlowStepsLog("");
    setFlowResult("Starting Implicit Flow with fragment response...");
    setActiveFlow("implicit");
    setFlowEvents([]); // Clear previous events

    const authority = getAuthorityForApp();
    appendFlowLog(`Using authority: ${authority}`, "system");

    try {
      // Add step numbering for clarity
      appendFlowLog("Step 1: Preparing Implicit Flow request", "normal");
      // Add a more nuanced warning log entry
      appendFlowLog("⚠️ You're testing Implicit Flow with fragment response (response_mode=fragment).", "warning");
      appendFlowLog("This flow returns tokens directly in the URL fragment and is discouraged for SPAs.", "warning");
      appendFlowLog("For web apps with a server backend, consider Implicit Flow with form_post instead.", "warning");
      
      // Configure the auth params to request the token directly
      appendFlowLog("Configuring request with response_type=token id_token", "normal");
      const request = {
        scopes: ["openid", "profile", "User.Read"],
        prompt: "select_account",
        responseMode: "fragment",
        extraQueryParameters: {
          response_type: "token id_token"
        }
      };
      
      // Use loginPopup, but the response will include tokens directly
      appendFlowLog("Initiating authorization request...", "normal");
      const loginResponse = await msalInstance.loginPopup(request);
      appendFlowLog("✓ Authentication successful", "success");  // Add this after login response
      appendFlowLog("✓ Access token and ID token received directly", "success"); 
      
      setTesterAccount(loginResponse.account || null);
      
      // Unlike Auth Code flow, tokens come directly in the response
      appendFlowLog("⚠️ SECURITY RISK: Access token was returned directly in URL fragment!", "warning");
      appendFlowLog("This exposes tokens to potential interception via browser history and referrer headers", "warning");
      
      // We already got the access token in the response
      const localDecoded = decodeJwtManually(loginResponse.accessToken);
      setDecodedToken(localDecoded);
      if (onDecodedTokenTested) {
        onDecodedTokenTested(localDecoded);
      }
      
      setFlowResult("Implicit Flow with fragment response completed.");
      appendFlowLog("ℹ️ Microsoft recommends Auth Code Flow with PKCE for SPAs instead of fragment response", "normal");
      appendFlowLog("ℹ️ For web apps with a server, Implicit Flow with form_post remains a valid option", "normal");
    } catch (error: any) {
      setFlowResult(`Implicit Flow FAILED\n${error.message}`);
      console.error("Implicit Flow error:", error);
      setActiveFlow("none");
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      {/* Header Card */}
      <Card
        variant="outlined"
        sx={{
          mb: 4,
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 2,
            backgroundColor: '#f4f8fb',
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
          }}
        >
          <Button 
            variant="outlined" 
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{
              mr: 2,
              borderRadius: 2,
              pl: 1.5,
              color: "text.secondary",
              borderColor: "divider",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.04)",
                borderColor: "primary.main",
                color: "primary.main"
              }
            }}
          >
            Back
          </Button>
          
          <Box
            sx={{
              borderLeft: '4px solid',
              borderColor: 'primary.main',
              pl: 2,
              py: 0.5,
            }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: 'primary.main',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                letterSpacing: '-0.02em',
                mb: 0.5,
              }}
            >
              OAuth Flow Tester
            </Typography>
            
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'text.primary',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                fontWeight: 500,
              }}
            >
              Testing flows for{' '}
              <Box 
                component="span" 
                sx={{ 
                  color: 'primary.main',
                  fontWeight: 700,
                  fontSize: '1.05em',
                }}
              >
                {app.displayName}
              </Box>
              {' '}
              <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.9em' }}>
                ({app.appId})
              </Box>
            </Typography>
          </Box>
        </Box>
      </Card>

    {/* Flow Cards Container */}
    <Grid container spacing={3}>

      {/* Left Column - with Authentication Flows on top and Token Details below */}
      <Grid item xs={12} md={7}>
        {/* Authentication Flows Card - unchanged */}
        <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
          <CardContent sx={{ p: 0 }}>
            <Typography
              variant="h6"
              sx={{
              px: 3,
              py: 2,
              borderBottom: '1px solid rgba(0,0,0,0.12)', // Slightly darker gray
              backgroundColor: '#f9f9f9', // Slightly darker gray background compared to #f9f9f9
              fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center'
              }}
            >
              <SecurityIcon sx={{ mr: 1, color: 'primary.main' }} />
              Authentication Flows
            </Typography>
              
              {/* 1) Authorization Code Flow (PKCE) */}
              <Accordion 
                disabled={activeFlow === "hybrid" || activeFlow === "implicit" || activeFlow === "clientCredentials"}
                sx={{ 
                  '&.Mui-expanded': { 
                    margin: 0 
                  },
                  '&::before': {
                    display: 'none'
                  }
                }}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: 'white',
                    '&.Mui-expanded': {
                      minHeight: 48,
                      borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CodeIcon 
                      sx={{ 
                        mr: 1.5, 
                        color: activeFlow === "authCode" ? 'success.main' : 'text.secondary' 
                      }} 
                    />
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                        fontSize: "1.1rem",
                        fontWeight: 600,
                        color: activeFlow === "authCode" ? 'success.main' : 'text.primary',
                        letterSpacing: "0.01em"
                      }}
                    >
                      Authorization Code Flow (PKCE)
                    </Typography>
                  </Box>
                </AccordionSummary>
                
                <AccordionDetails sx={{ backgroundColor: "rgba(232, 245, 233, 0.2)", p: 3 }}>
                  <Box sx={{ 
                    mb: 3, 
                    p: 2, 
                    borderRadius: 1, 
                    backgroundColor: 'rgba(232, 245, 233, 0.7)',
                    border: '1px solid rgba(76, 175, 80, 0.2)'
                  }}>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center' 
                      }}
                    >
                      <InfoOutlinedIcon sx={{ mr: 1, fontSize: '0.9rem', color: 'success.main' }} />
                      MSAL will open a popup for sign-in, then redeem the authorization code for an access token.
                    </Typography>
                  </Box>

                  <Button 
                    variant="contained" 
                    onClick={testAuthCodeFlow} 
                    disabled={
                      activeFlow === "hybrid" || 
                      activeFlow === "implicit" || 
                      activeFlow === "clientCredentials" || 
                      (activeFlow === "authCode" && testerAccount !== null) // Add this condition
                    }
                    sx={{ 
                      mr: 2,
                      borderRadius: 2,
                      boxShadow: '0 2px 4px rgba(76, 175, 80, 0.2)',
                      backgroundColor: '#4caf50',
                      '&:hover': {
                        backgroundColor: '#388e3c'
                      }
                    }}
                    startIcon={<DoubleArrowIcon />}
                  >
                    {activeFlow === "authCode" && testerAccount ? "Already Authenticated" : "Start Auth Code Flow"}
                  </Button>

                  {testerAccount && activeFlow === "authCode" && (
                    <Box 
                      sx={{ 
                        mt: 3,
                        p: 2,
                        borderRadius: 1,
                        border: '1px solid rgba(0,0,0,0.1)',
                        backgroundColor: '#f9f9f9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="body1">
                          Signed in as: <strong>{testerAccount.username}</strong>
                        </Typography>
                      </Box>
                      
                      <Button 
                        variant="outlined" 
                        color="error" 
                        onClick={handleSignOut}
                        size="small"
                        sx={{ borderRadius: 2 }}
                      >
                        Sign Out
                      </Button>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>

              {/* 2) Client Credentials Flow */}
              <Accordion 
                disabled={activeFlow === "authCode" || activeFlow === "hybrid" || activeFlow === "implicit"}
                sx={{ 
                  '&.Mui-expanded': { 
                    margin: 0 
                  },
                  '&::before': {
                    display: 'none'
                  }
                }}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: 'white',
                    '&.Mui-expanded': {
                      minHeight: 48,
                      borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <VpnKeyIcon 
                      sx={{ 
                        mr: 1.5, 
                        color: activeFlow === "clientCredentials" ? 'warning.main' : 'text.secondary' 
                      }} 
                    />
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                        fontSize: "1.1rem",
                        fontWeight: 600,
                        color: activeFlow === "clientCredentials" ? 'warning.main' : 'text.primary',
                        letterSpacing: "0.01em"
                      }}
                    >
                      Client Credentials Flow
                    </Typography>
                  </Box>
                </AccordionSummary>
                
                <AccordionDetails sx={{ backgroundColor: "rgba(255, 249, 196, 0.2)", p: 3 }}>
                  <Box sx={{ 
                    mb: 3, 
                    p: 2, 
                    borderRadius: 1, 
                    backgroundColor: 'rgba(255, 249, 196, 0.7)',
                    border: '1px solid rgba(255, 193, 7, 0.2)'
                  }}>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center' 
                      }}
                    >
                      <InfoOutlinedIcon sx={{ mr: 1, fontSize: '0.9rem', color: 'warning.main' }} />
                      For confidential clients only. Provide the client secret below.
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                  <TextField
                    type="password"
                    label="Client Secret"
                    value={clientSecret}
                    onChange={(e) => setClientSecret(e.target.value)}
                    disabled={activeFlow !== "none" && activeFlow !== "clientCredentials"}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: 'warning.main',
                        },
                      }
                    }}
                  />
                    <Button 
                      variant="outlined" 
                      onClick={() => setClientSecret("")}
                      sx={{ borderRadius: 2 }}
                    >
                      Clear
                    </Button>
                  </Box>

                  {activeFlow === "clientCredentials" ? (
                    <Button 
                      variant="outlined" 
                      color="error" 
                      onClick={handleExitClientCredsFlow}
                      sx={{ borderRadius: 2 }}
                    >
                      Exit Client Creds Flow
                    </Button>
                  ) : (
                    <Button 
                      variant="contained" 
                      onClick={testClientCredentialsFlow}
                      disabled={activeFlow === "authCode" || activeFlow === "hybrid" || activeFlow === "implicit"}
                      sx={{ 
                        borderRadius: 2,
                        boxShadow: '0 2px 4px rgba(255, 193, 7, 0.2)',
                        backgroundColor: '#ff9800',
                        '&:hover': {
                          backgroundColor: '#f57c00'
                        }
                      }}
                      startIcon={<DoubleArrowIcon />}
                    >
                      Start Client Credentials Flow
                    </Button>
                  )}
                </AccordionDetails>
              </Accordion>

              {/* 3) Hybrid Flow (Simulation) */}
              <Accordion 
                disabled={activeFlow === "authCode" || activeFlow === "implicit" || activeFlow === "clientCredentials"}
                sx={{ 
                  '&.Mui-expanded': { 
                    margin: 0 
                  },
                  '&::before': {
                    display: 'none'
                  }
                }}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: 'white',
                    '&.Mui-expanded': {
                      minHeight: 48,
                      borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <VerifiedUserIcon 
                      sx={{ 
                        mr: 1.5, 
                        color: activeFlow === "hybrid" ? 'info.main' : 'text.secondary' 
                      }} 
                    />
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                        fontSize: "1.1rem",
                        fontWeight: 600,
                        color: activeFlow === "hybrid" ? 'info.main' : 'text.primary',
                        letterSpacing: "0.01em"
                      }}
                    >
                      Hybrid Flow (Simulation)
                    </Typography>
                  </Box>
                </AccordionSummary>
                
                <AccordionDetails sx={{ backgroundColor: "rgba(241, 248, 233, 0.2)", p: 3 }}>
                {/* Combined info box with title and both pieces of information */}
                <Box sx={{ 
                  mb: 3, 
                  p: 2, 
                  borderRadius: 1, 
                  backgroundColor: 'rgba(241, 248, 233, 0.7)',
                  border: '1px solid rgba(104, 159, 56, 0.2)'
                }}>
                  {/* Always visible part */}
                  <Box 
                    onClick={() => setHybridDetailsExpanded(!hybridDetailsExpanded)}
                    sx={{ 
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <Typography 
                      variant="subtitle2" 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        fontWeight: 600,
                        color: '#1976d2',
                        mb: 1
                      }}
                    >
                      <InfoOutlinedIcon sx={{ mr: 1, fontSize: '1rem', color: 'info.main' }} />
                      Hybrid Flow Usage
                      <Typography 
                        variant="caption" 
                        color="primary" 
                        sx={{ ml: 'auto', fontSize: '0.75rem' }}
                      >
                        {hybridDetailsExpanded ? 'Hide details' : 'Show details'}
                      </Typography>
                    </Typography>
                    
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Requests both an authorization code and an ID token in a single round-trip.
                      MSAL still acquires the access token by redeeming the code.
                      <br />
                      <Box component="span" sx={{ color: 'error.main' }}>
                      Not recommended for pure frontend apps without a secure backend!
                      </Box>          
                    </Typography>
                  </Box>

                  {/* Expandable details */}
                  {hybridDetailsExpanded && (
                    <Box sx={{ mt: 1 }}>
                      <Divider sx={{ my: 1.5 }} />
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', mb: 0.5 }}>
                            Recommended for:
                          </Typography>
                          <ul style={{ margin: 0, paddingLeft: 18 }}>
                            <li>Web apps with a secure backend</li>
                            <li>Need immediate identity before code exchange</li>
                            <li>SPAs with server-side token storage</li>
                          </ul>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#d32f2f', mb: 0.5 }}>
                            Not recommended for:
                          </Typography>
                          <ul style={{ margin: 0, paddingLeft: 18 }}>
                            <li>Pure frontend apps without a backend</li>
                            <li>Mobile or desktop applications</li>
                            <li>Browser-only token storage scenarios</li>
                          </ul>
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                </Box>

                <Button 
                  variant="contained" 
                  onClick={testHybridFlow}
                  disabled={
                    activeFlow === "authCode" || 
                    activeFlow === "implicit" || 
                    activeFlow === "clientCredentials" || 
                    (activeFlow === "hybrid" && testerAccount !== null) // Add this condition
                  }
                  sx={{ 
                    borderRadius: 2,
                    boxShadow: '0 2px 4px rgba(33, 150, 243, 0.2)',
                    backgroundColor: '#2196f3',
                    '&:hover': {
                      backgroundColor: '#1976d2'
                    }
                  }}
                  startIcon={<DoubleArrowIcon />}
                >
                  {activeFlow === "hybrid" && testerAccount ? "Already Authenticated" : "Start Hybrid Flow"}
                </Button>

                  {testerAccount && activeFlow === "hybrid" && (
                    <Box 
                      sx={{ 
                        mt: 3,
                        p: 2,
                        borderRadius: 1,
                        border: '1px solid rgba(0,0,0,0.1)',
                        backgroundColor: '#f9f9f9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="body1">
                          Signed in as: <strong>{testerAccount.username}</strong>
                        </Typography>
                      </Box>
                      
                      <Button 
                        variant="outlined" 
                        color="error" 
                        onClick={handleSignOut}
                        size="small"
                        sx={{ borderRadius: 2 }}
                      >
                        Sign Out
                      </Button>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>

              {/* 4) Implicit Flow (Simulation) */}
              <Accordion 
                disabled={activeFlow === "authCode" || activeFlow === "hybrid" || activeFlow === "clientCredentials"}
                sx={{ 
                  '&.Mui-expanded': { 
                    margin: 0 
                  },
                  '&::before': {
                    display: 'none'
                  }
                }}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: 'white',
                    '&.Mui-expanded': {
                      minHeight: 48,
                      borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ErrorOutlineIcon 
                      sx={{ 
                        mr: 1.5, 
                        color: activeFlow === "implicit" ? 'error.main' : 'text.secondary' 
                      }} 
                    />
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                        fontSize: "1.1rem",
                        fontWeight: 600,
                        color: activeFlow === "implicit" ? 'error.main' : 'text.primary',
                        letterSpacing: "0.01em"
                      }}
                    >
                      Implicit Flow (Simulation)
                    </Typography>
                  </Box>
                </AccordionSummary>
                
                <AccordionDetails sx={{ backgroundColor: "rgba(255, 235, 238, 0.2)", p: 3 }}>
                {/* Combined info box with title and expandable information */}
                <Box sx={{ 
                  mb: 3, 
                  p: 2, 
                  borderRadius: 1, 
                  backgroundColor: 'rgba(255, 235, 238, 0.7)',
                  border: '1px solid rgba(244, 67, 54, 0.2)'
                }}>
                  {/* Always visible part */}
                  <Box 
                    onClick={() => setImplicitDetailsExpanded(!implicitDetailsExpanded)}
                    sx={{ 
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <Typography 
                      variant="subtitle2" 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        fontWeight: 600,
                        color: '#d32f2f',
                        mb: 1
                      }}
                    >
                      <InfoOutlinedIcon sx={{ mr: 1, fontSize: '1rem', color: 'error.main' }} />
                      Important Guidance
                      <Typography 
                        variant="caption" 
                        color="error" 
                        sx={{ ml: 'auto', fontSize: '0.75rem' }}
                      >
                        {implicitDetailsExpanded ? 'Hide details' : 'Show details'}
                      </Typography>
                    </Typography>
                    
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Implicit Flow with fragment response</strong> (response_mode=fragment) is discouraged for SPAs due to security vulnerabilities.
                    </Typography>
                    <Typography variant="body2">
                      <strong>Implicit Flow with form post</strong> (response_mode=form_post) remains a valid choice for web applications with server-side processing.
                    </Typography>
                  </Box>

                  {/* Expandable details */}
                  {implicitDetailsExpanded && (
                    <Box sx={{ mt: 2 }}>
                      <Divider sx={{ my: 1.5 }} />
                      
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#d32f2f' }}>
                        Flow Variations:
                      </Typography>

                      <Box sx={{ ml: 2, mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'flex-start' }}>
                          <Box component="span" sx={{ 
                            color: 'error.main', 
                            mr: 1, 
                            fontWeight: 'bold',
                            fontSize: '1.2rem',
                            lineHeight: 1
                          }}>•</Box>
                          <Box>
                            <strong>Fragment Response:</strong> Returns tokens directly in URL fragment (not recommended for SPAs)
                          </Box>
                        </Typography>
                        
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'flex-start' }}>
                          <Box component="span" sx={{ 
                            color: 'success.main', 
                            mr: 1, 
                            fontWeight: 'bold',
                            fontSize: '1.2rem',
                            lineHeight: 1
                          }}>•</Box>
                          <Box>
                            <strong>Form Post Response:</strong> Sends tokens via POST to backend (suitable for web apps with server)
                          </Box>
                        </Typography>
                      </Box>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#388e3c', mb: 0.5 }}>
                            Recommended for (form_post):
                          </Typography>
                          <ul style={{ margin: 0, paddingLeft: 18 }}>
                            <li>Web apps with a server backend</li>
                            <li>Classic MVC web applications</li>
                            <li>Legacy applications that need simpler implementation</li>
                          </ul>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#d32f2f', mb: 0.5 }}>
                            Not recommended for:
                          </Typography>
                          <ul style={{ margin: 0, paddingLeft: 18 }}>
                            <li>SPAs that store tokens in browser</li>
                            <li>Mobile applications</li>
                            <li>Desktop applications</li>
                            <li>Any modern application with security requirements</li>
                          </ul>
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                </Box>

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button 
                    variant="contained" 
                    onClick={testImplicitFlow}
                    disabled={
                      activeFlow === "authCode" || 
                      activeFlow === "hybrid" || 
                      activeFlow === "clientCredentials" || 
                      (activeFlow === "implicit" && testerAccount !== null) // Add this condition
                    }
                    sx={{ 
                      borderRadius: 2,
                      boxShadow: '0 2px 4px rgba(244, 67, 54, 0.2)',
                      backgroundColor: '#f44336',
                      '&:hover': {
                        backgroundColor: '#d32f2f'
                      }
                    }}
                    startIcon={<DoubleArrowIcon />}
                  >
                    {activeFlow === "implicit" && testerAccount ? "Already Authenticated" : "Test Fragment Response"}
                  </Button>

                  <Button 
                    variant="outlined" 
                    onClick={() => alert("Form Post response requires a server to receive the tokens.\n\nThis is typically implemented in web applications where tokens are processed server-side.")}
                    disabled={
                      activeFlow === "authCode" || 
                      activeFlow === "hybrid" || 
                      activeFlow === "clientCredentials" || 
                      (activeFlow === "implicit" && testerAccount !== null) // Add this condition
                    }
                    sx={{ 
                      borderRadius: 2,
                      borderColor: '#4caf50',
                      color: '#4caf50',
                      '&:hover': {
                        borderColor: '#2e7d32',
                        backgroundColor: 'rgba(76, 175, 80, 0.04)'
                      }
                    }}
                  >
                    About Form Post Response
                  </Button>
                </Box>

                  {/* Sign Out button for Implicit Flow when user is authenticated */}
                  {testerAccount && activeFlow === "implicit" && (
                    <Box 
                      sx={{ 
                        mt: 3,
                        p: 2,
                        borderRadius: 1,
                        border: '1px solid rgba(0,0,0,0.1)',
                        backgroundColor: '#f9f9f9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="body1">
                          Signed in as: <strong>{testerAccount.username}</strong>
                        </Typography>
                      </Box>
                      
                      <Button 
                        variant="outlined" 
                        color="error" 
                        onClick={handleSignOut}
                        size="small"
                        sx={{ borderRadius: 2 }}
                      >
                        Sign Out
                      </Button>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>

          </CardContent>
        </Card>
    

        {/* MOVED: Token Viewer Card to Left Column */}
        <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
          <CardContent sx={{ p: 0 }}>
            <Typography
              variant="h6"
              sx={{
                px: 3,
                py: 2,
                borderBottom: '1px solid rgba(0,0,0,0.08)',
                backgroundColor: '#f9f9f9',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <TokenIcon sx={{ mr: 1, color: 'primary.main' }} />
              Token Details
            </Typography>
            
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    if (!decodedToken) {
                      alert("No token to show. Run a flow first!");
                      return;
                    }
                    setIsTokenVisible(!isTokenVisible);
                  }}
                  disabled={!decodedToken}
                  startIcon={isTokenVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  sx={{ borderRadius: 2 }}
                >
                  {isTokenVisible ? "Hide Token" : "Show Token"}
                </Button>

                {decodedToken && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => {
                      setDecodedToken(null);
                      setFlowResult("");
                      setIsTokenVisible(false);
                      if (onDecodedTokenTested) {
                        onDecodedTokenTested(null);
                      }
                    }}
                    sx={{ borderRadius: 2 }}
                  >
                    Clear Token
                  </Button>
                )}
              </Box>

              {!decodedToken ? (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  No token available. Complete an authentication flow to see token details.
                </Alert>
              ) : !isTokenVisible ? (
                <Alert severity="success" sx={{ borderRadius: 2 }}>
                  Token acquired. Click "Show Token" to view details.
                </Alert>
              ) : (
                <Box>
                  {/* Token Summary Box */}
                  <Box 
                    sx={{ 
                      p: 2, 
                      mb: 2, 
                      borderRadius: 1,
                      backgroundColor: '#f3e5f5',
                      border: '1px solid #e1bee7'
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'secondary.dark' }}>
                      Token Summary:
                    </Typography>
                    
                    <Grid container spacing={1}>
                      <Grid item xs={4} sm={3}>
                        <Typography variant="body2" fontWeight="bold" color="text.secondary">
                          Issued to:
                        </Typography>
                      </Grid>
                      <Grid item xs={8} sm={9}>
                        <Typography variant="body2">
                          {decodedToken.name || decodedToken.upn || "Unknown"}
                        </Typography>
                      </Grid>

                      <Grid item xs={4} sm={3}>
                        <Typography variant="body2" fontWeight="bold" color="text.secondary">
                          Audience:
                        </Typography>
                      </Grid>
                      <Grid item xs={8} sm={9}>
                        <Typography variant="body2">
                          {decodedToken.aud || "Unknown"}
                        </Typography>
                      </Grid>

                      <Grid item xs={4} sm={3}>
                        <Typography variant="body2" fontWeight="bold" color="text.secondary">
                          Scopes:
                        </Typography>
                      </Grid>
                      <Grid item xs={8} sm={9}>
                        <Typography variant="body2">
                          {decodedToken.scp || "None specified"}
                        </Typography>
                      </Grid>

                      <Grid item xs={4} sm={3}>
                        <Typography variant="body2" fontWeight="bold" color="text.secondary">
                          Expires:
                        </Typography>
                      </Grid>
                      <Grid item xs={8} sm={9}>
                        <Typography variant="body2">
                          {decodedToken.exp
                            ? new Date(decodedToken.exp * 1000).toLocaleString()
                            : "Unknown"}
                        </Typography>
                      </Grid>

                      <Grid item xs={4} sm={3}>
                        <Typography variant="body2" fontWeight="bold" color="text.secondary">
                          Issuer:
                        </Typography>
                      </Grid>
                      <Grid item xs={8} sm={9}>
                        <Typography variant="body2">
                          {decodedToken.iss || "Unknown issuer"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Full Token Details - takes advantage of wider left column */}
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Complete Token Contents:
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      bgcolor: "#f7f7f7",
                      maxHeight: "500px", // Increased height with wider column
                      overflow: "auto",
                      borderRadius: 1
                    }}
                  >
                    <pre style={{ margin: 0 }}>{JSON.stringify(decodedToken, null, 2)}</pre>
                  </Paper>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>

      </Grid>

      {/* Right Column - with Flow Status */}
      <Grid item xs={12} md={5}>
        {/* Flow Status Card - now the only card on the right */}
        <Card variant="outlined" sx={{ borderRadius: 2, mb: 3, height: 'auto' }}>
          <CardContent sx={{ p: 0 }}>
            <Typography
              variant="h6"
              sx={{
                px: 3,
                py: 2,
                borderBottom: '1px solid rgba(0,0,0,0.08)',
                backgroundColor: '#f9f9f9',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <AssessmentIcon sx={{ mr: 1, color: 'primary.main' }} />
              Flow Status
            </Typography>
            
            <Box sx={{ p: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Status:</Typography>              
              {activeFlow !== "none" && (
                <Chip 
                  label={`Active: ${
                    activeFlow === "authCode" ? "Auth Code Flow" : 
                    activeFlow === "hybrid" ? "Hybrid Flow" : 
                    activeFlow === "implicit" ? "Implicit Flow" :
                    "Client Credentials Flow"
                  }`} 
                  color={
                    activeFlow === "authCode" ? "success" : 
                    activeFlow === "hybrid" ? "primary" : 
                    activeFlow === "implicit" ? "error" :
                    "warning"
                  }
                  sx={{ mb: 2 }}
                />
              )}           
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: "#f5f5f5",
                  p: 2,
                  borderRadius: 1,
                  whiteSpace: "pre-wrap",
                  overflowX: "auto",
                  border: '1px solid rgba(0,0,0,0.08)'
                }}
              >
                {flowResult}
              </Paper>

              {/* MSAL Events - Real event tracking */}
              {flowEvents.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                    <AssessmentIcon sx={{ mr: 1, fontSize: '1rem' }} />
                    MSAL Events:
                  </Typography>
                  <Paper
                    elevation={0}
                    sx={{
                      backgroundColor: "#fafafa",
                      p: 2,
                      borderRadius: 1,
                      maxHeight: 300,
                      overflowY: 'auto',
                      border: '1px solid rgba(0,0,0,0.08)'
                    }}
                  >
                    {flowEvents.map((event, index) => {
                      const eventColor = 
                        event.eventType.includes('SUCCESS') ? '#4caf50' :
                        event.eventType.includes('FAILURE') || event.eventType.includes('ERROR') ? '#f44336' :
                        event.eventType.includes('START') ? '#2196f3' :
                        '#757575';
                      
                      return (
                        <Box 
                          key={index} 
                          sx={{ 
                            mb: 1, 
                            pb: 1, 
                            borderBottom: index < flowEvents.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none'
                          }}
                        >
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 600, 
                              color: eventColor,
                              fontFamily: 'monospace'
                            }}
                          >
                            {event.eventType}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                            {event.timestamp.toLocaleTimeString()} 
                            {event.correlationId && ` • ${event.correlationId.substring(0, 8)}...`}
                          </Typography>
                          {event.message && event.message !== "Event occurred" && (
                            <Typography variant="caption" sx={{ color: 'text.primary', display: 'block', mt: 0.5 }}>
                              {event.message}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Paper>
                </Box>
              )}

              {/* Detailed Flow Log */}
              {(activeFlow === "authCode" || activeFlow === "hybrid" || activeFlow === "implicit") && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                    <FolderOpenIcon sx={{ mr: 1, fontSize: '1rem' }} />
                    Flow Events:
                  </Typography>
                  <Paper
                    elevation={0}
                    sx={{
                      backgroundColor: "#f0f8ff",
                      p: 2,
                      borderRadius: 1,
                      whiteSpace: "pre-wrap",
                      overflowX: "auto",
                      maxHeight: 600, // Increased height for better visibility
                      overflowY: 'auto',
                      border: activeFlow === "implicit" 
                        ? '1px solid rgba(244, 67, 54, 0.2)' 
                        : '1px solid rgba(25, 118, 210, 0.2)'
                    }}
                  >
                    {flowStepsLog.split('\n').map((line, index) => {
                      if (line.startsWith('<system>') && line.endsWith('</system>')) {
                        return (
                          <div key={index} style={{ color: '#757575', fontStyle: 'italic' }}>
                            {line.replace('<system>', '').replace('</system>', '')}
                          </div>
                        );
                      } else if (line.startsWith('<warning>') && line.endsWith('</warning>')) {
                        return (
                          <div key={index} style={{ color: '#d32f2f', fontWeight: 500 }}>
                            {line.replace('<warning>', '').replace('</warning>', '')}
                          </div>
                        );
                      } else if (line.startsWith('<success>') && line.endsWith('</success>')) {
                        return (
                          <div key={index} style={{ color: '#388e3c', fontWeight: 500 }}>
                            {line.replace('<success>', '').replace('</success>', '')}
                          </div>
                        );
                      }
                      return <div key={index}>{line}</div>;
                    })}
                  </Paper>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </Grid>  
    </Grid>

      {/* About OAuth Flow Tester - Expandable Accordion */}
      <Card variant="outlined" sx={{ borderRadius: 2, mt: 3 }}>
        <Accordion expanded={aboutExpanded} onChange={() => setAboutExpanded(!aboutExpanded)}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <InfoOutlinedIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                About OAuth Flow Tester
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={3}>
              <Typography variant="body2" color="text.secondary">
                This tool tests OAuth 2.0 / OpenID Connect authentication flows for your Entra ID registered applications using the MSAL.js library. 
                Select from various flow types to understand how your application authenticates users and obtains tokens.
              </Typography>
              
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                  Supported OAuth Flows:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'rgba(232, 245, 233, 0.3)', borderColor: '#4caf50' }}>
                      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#2e7d32' }}>
                        Authorization Code Flow (PKCE)
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        The recommended flow for browser-based apps. Uses Proof Key for Code Exchange (PKCE) to securely exchange an authorization code for tokens 
                        without exposing client secrets to the browser. MSAL 2.0 uses this by default.
                      </Typography>
                    </Paper>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'rgba(227, 242, 253, 0.3)', borderColor: '#2196f3' }}>
                      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#1976d2' }}>
                        Hybrid Flow (Simulation)
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Simulated by MSAL 2.0.</strong> In a true Hybrid flow, both ID Token and Authorization Code are returned together. 
                        MSAL 2.0 doesn't perform this protocol-level exchange—it uses Auth Code + PKCE internally.
                      </Typography>
                    </Paper>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'rgba(255, 243, 224, 0.3)', borderColor: '#ff9800' }}>
                      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#e65100' }}>
                        Client Credentials Flow
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        For service-to-service authentication with no user context. Uses client ID and secret to directly obtain access tokens for APIs and secure resources. 
                        Requires a confidential client (application with a client secret).
                      </Typography>
                    </Paper>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'rgba(255, 235, 238, 0.3)', borderColor: '#f44336' }}>
                      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#d32f2f' }}>
                        Implicit Flow (Simulation, Deprecated)
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>⚠️ Deprecated and simulated.</strong> True Implicit flow returns tokens in URL fragment, but MSAL 2.0 uses Auth Code + PKCE internally. 
                        Not recommended for production due to security concerns.
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                  Key Features:
                </Typography>
                <Stack spacing={0.5}>
                  <Typography variant="body2" color="text.secondary">
                    • <strong>Real MSAL Events:</strong> See actual MSAL.js events with timestamps and correlation IDs
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • <strong>Token Details:</strong> View decoded JWT claims, expiration, scopes, and issuer information
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • <strong>Flow Status:</strong> Track authentication progress with detailed event logging
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • <strong>Interactive Testing:</strong> Test flows in popup windows with proper CSRF and replay protection
                  </Typography>
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'warning.main' }}>
                  ⚠️ MSAL 2.0 Behavior Notice:
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Hybrid and Implicit flows are simulations.</strong> MSAL 2.0 doesn't perform true protocol-level Hybrid (code + id_token) 
                  or Implicit (token in fragment) exchanges. Instead, it internally uses Authorization Code + PKCE for all browser-based flows.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  For authentic protocol-level testing of these flows, use <strong>Raw OAuth Tester</strong> from the main Tools menu. 
                  To see what MSAL actually does behind the scenes, use <strong>MSAL Trace Viewer</strong>.
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'info.main' }}>
                  Prerequisites:
                </Typography>
                <Stack spacing={0.5}>
                  <Typography variant="body2" color="text.secondary">
                    • Application must be registered in Entra ID (Azure AD)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Redirect URIs must be configured in the app registration
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • For Implicit/Hybrid flows: Enable ID tokens under "Implicit grant and hybrid flows"
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • For Client Credentials: Requires a client secret (confidential client)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • User must have permissions to sign in to the application
                  </Typography>
                </Stack>
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Card>

    </Container>
  );
};

export default AuthFlowTester;